/**
 * Feature E04-F02 — "Inbox: link / create / unlink / dismiss / restore / reply"
 * request/response contracts (E04-F02-T01..T07).
 *
 * 1:1 port of the legacy n8n Inbox + Project + branch-7 actions:
 *   - T01 link_to_project        (reference/Inbox.md  → "Inbox: Link to Project")
 *   - T02 create_from_email      (reference/Project.md / Create.md, branch-7 LLM)
 *   - T03 unlink_from_project     (reference/Inbox.md → "Inbox: Unlink from Project")
 *         dismiss / restore        (branch-7: meta.dismissed toggle)
 *   - T04 send_reply              (reference/Inbox.md → "Inbox: Send Reply", SMTP)
 *   - T07 cc_emails extraction    (n8n CC1 extractCcEmails → projects.data.cc_emails)
 *
 * IDOR invariant (carried over unchanged from the n8n `_common`): the actor and
 * the owning project are derived from the SESSION user id by the route, NEVER
 * from a body field. No input schema here accepts a `user_id` / `owner_id`, so a
 * request can never forge ownership.
 *
 * Lives in its OWN file — NOT re-exported from the shared barrel by this agent;
 * the serial Wire pass adds the `export * from './contracts/inbox-actions.js'`
 * line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// shared — email id path param + generic success envelope
// ---------------------------------------------------------------------------
/** `:id` path param — must coerce to a positive integer (email id). */
export const emailIdParamSchema = z.object({
    id: z.coerce.number().int().positive(),
});
/** The legacy `{ success: true }` reply every write action returns. */
export const inboxActionSuccessSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
});
// ---------------------------------------------------------------------------
// T01 — POST /emails/:id/link  (link_to_project)
// ---------------------------------------------------------------------------
/**
 * Body for linking an email to an EXISTING project (Inbox.md "Link to Project").
 * The email id comes from the path; only the target project id is in the body.
 * Legacy parity: missing project_id → `{ success:false, error:'missing email_id
 * or project_id' }` (the route surfaces that as a 400 VALIDATION_ERROR).
 */
export const linkEmailBodySchema = z.object({
    project_id: z.coerce.number().int().positive(),
});
// ---------------------------------------------------------------------------
// T02 / T07 — POST /emails/:id/create-project  (create_from_email)
// ---------------------------------------------------------------------------
/**
 * Body for creating a project FROM an email (create_from_email). No required
 * fields — everything is derived from the email + the LLM extraction + the
 * session. `cc_emails` MAY be supplied by the client (pre-parsed header), else
 * the service extracts CC from the email/extraction (T07).
 */
export const createProjectFromEmailBodySchema = z
    .object({
    /** Optional client-supplied CC list (already-parsed). Service still
     * normalizes + validates them (T07). */
    cc_emails: z.array(z.string()).optional(),
})
    .partial();
/** Success reply of create_from_email — carries the new project id (Project.md). */
export const createProjectFromEmailResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    project_id: z.number().int().positive(),
});
// ---------------------------------------------------------------------------
// T03 — POST /emails/:id/{unlink,dismiss,restore}
// ---------------------------------------------------------------------------
// unlink / dismiss / restore take only the email id from the path → no body.
// ---------------------------------------------------------------------------
// T04 — POST /emails/:id/reply  (send_reply via SMTP)
// ---------------------------------------------------------------------------
/**
 * Body for replying to an email (Inbox.md "Send Reply"). `to`/`subject`/`body`
 * are required; `in_reply_to` and `thread_id` thread the reply to the original.
 * The SENDER is the system mailbox (user_id='system') — never a body field
 * (Inbox.md gotcha: From = mailbox.email, not the body).
 */
export const replyEmailBodySchema = z.object({
    to: z.string().trim().min(1, 'recipient required'),
    subject: z.string().default(''),
    /** Plain-text body (newlines → <br>). Must be non-empty (T04 / T05 parity). */
    body: z.string().trim().min(1, 'body required'),
    /** Original external_id for In-Reply-To / Reply-To (optional). */
    in_reply_to: z.string().optional(),
    /** Thread id to persist on the stored outbound email (optional). */
    thread_id: z.string().optional(),
});
// ---------------------------------------------------------------------------
// T07 — raw CC extraction shapes (CC1 extractCcEmails)
// ---------------------------------------------------------------------------
/**
 * One CC entry as it can arrive from the mailbox layer: either an object with an
 * `.address` (Graph/IMAP parsed recipients) or a bare string. The header string
 * form (`'Name <email>, email2'`) is handled separately by `extractCcEmails`.
 */
export const ccEntryObjectSchema = z
    .object({
    address: z.string().nullish(),
    name: z.string().nullish(),
})
    .passthrough();
//# sourceMappingURL=inbox-actions.js.map